/**
 * Complex Process Manager - REFACTORED VERSION
 * 
 * This version uses the new data layer architecture with hooks
 * instead of direct GraphQL client calls
 */

import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Modal,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import {
    IconCheck,
    IconCopy,
    IconDatabase,
    IconEdit,
    IconPlus,
    IconRefresh,
    IconTemplate,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';

// Import from our new data layer
import {
    ComplexProcessDefinition,
    useProcesses,
    useProcessMutations,
    useProcessTemplates
} from '../data';

interface ComplexProcessManagerProps {
    onProcessSelect?: (process: ComplexProcessDefinition) => void;
    onProcessCreate?: (process: ComplexProcessDefinition) => void;
    onProcessDelete?: (processId: string) => void;
}

export const ComplexProcessManagerRefactored: React.FC<ComplexProcessManagerProps> = ({
    onProcessSelect,
    onProcessCreate,
    onProcessDelete
}) => {
    // State
    const [selectedDomain, setSelectedDomain] = useState<string>('');
    const [viewMode, setViewMode] = useState<'all' | 'templates' | 'custom'>('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedProcess, setSelectedProcess] = useState<ComplexProcessDefinition | null>(null);

    // Use our new data layer hooks
    const {
        processes,
        loading,
        error,
        refetch
    } = useProcesses({
        filter: selectedDomain ? { domains: [selectedDomain] } as any : undefined
    });

    const {
        templates,
        loading: templatesLoading
    } = useProcessTemplates(selectedDomain);

    const {
        createProcess,
        updateProcess,
        deleteProcess,
        cloneTemplate,
        loading: mutationLoading
    } = useProcessMutations();

    // Form for creating new process
    const form = useForm({
        initialValues: {
            name: '',
            domain: 'Healthcare',
            description: '',
            complexity: 'Medium' as ComplexProcessDefinition['complexity'],
            isTemplate: false
        },
        validate: {
            name: (value: string) => !value ? 'Name is required' : null,
            domain: (value: string) => !value ? 'Domain is required' : null,
            description: (value: string) => !value ? 'Description is required' : null
        }
    });

    // Handle error display
    useEffect(() => {
        if (error) {
            showNotification({
                title: 'Error',
                message: error.message || 'Failed to load processes',
                color: 'red',
                icon: <IconX />
            });
        }
    }, [error]);

    // Handlers using the new data layer
    const handleCreateProcess = async (values: typeof form.values) => {
        try {
            const newProcess = await createProcess({
                ...values,
                stages: [],
                connections: [],
                externalSystems: [],
                actors: [],
                metrics: {
                    totalSteps: 0,
                    uniqueActors: 0,
                    loopingStages: 0,
                    externalIntegrations: 0,
                    decisionPoints: 0
                }
            });

            showNotification({
                title: 'Success',
                message: 'Process created successfully',
                color: 'green',
                icon: <IconCheck />
            });

            setShowCreateModal(false);
            form.reset();

            if (onProcessCreate) {
                onProcessCreate(newProcess);
            }
        } catch (error) {
            // Error handling is done by the hook
            console.error('Failed to create process:', error);
        }
    };

    const handleCloneTemplate = async (templateId: string) => {
        const newName = prompt('Enter name for the new process:');
        if (!newName) return;

        try {
            await cloneTemplate(templateId, newName);

            showNotification({
                title: 'Success',
                message: 'Template cloned successfully',
                color: 'green',
                icon: <IconCheck />
            });
        } catch (error) {
            console.error('Failed to clone template:', error);
        }
    };

    const handleDeleteProcess = async (id: string) => {
        if (!confirm('Are you sure you want to delete this process?')) return;

        try {
            await deleteProcess(id);

            showNotification({
                title: 'Success',
                message: 'Process deleted successfully',
                color: 'green',
                icon: <IconCheck />
            });

            if (onProcessDelete) {
                onProcessDelete(id);
            }
        } catch (error) {
            console.error('Failed to delete process:', error);
        }
    };

    // Filter processes based on view mode
    const displayedProcesses = viewMode === 'all'
        ? processes
        : viewMode === 'templates'
            ? templates
            : processes.filter(p => !p.isTemplate);

    // Get unique domains
    const domains = [...new Set(processes.map(p => p.domain))];

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                {/* Header */}
                <Group justify="space-between" mb="xl">
                    <div>
                        <Group>
                            <IconDatabase size={28} color="var(--mantine-color-blue-6)" />
                            <div>
                                <Title order={2}>Complex Process Library</Title>
                                <Text c="dimmed">
                                    Powered by new data layer architecture
                                </Text>
                            </div>
                        </Group>
                    </div>

                    <Group>
                        <Button
                            leftSection={<IconRefresh />}
                            variant="subtle"
                            onClick={() => refetch()}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            leftSection={<IconPlus />}
                            onClick={() => setShowCreateModal(true)}
                        >
                            New Process
                        </Button>
                    </Group>
                </Group>

                {/* Controls */}
                <Group mb="md">
                    <Select
                        label="Domain Filter"
                        placeholder="All Domains"
                        data={['All', ...domains]}
                        value={selectedDomain || 'All'}
                        onChange={(value: string | null) => setSelectedDomain(value === 'All' ? '' : value || '')}
                        clearable
                        w={200}
                    />

                    <SegmentedControl
                        value={viewMode}
                        onChange={(value: any) => setViewMode(value)}
                        data={[
                            { label: 'All Processes', value: 'all' },
                            { label: 'Templates', value: 'templates' },
                            { label: 'Custom', value: 'custom' }
                        ]}
                    />
                </Group>

                {/* Stats */}
                <Group mb="xl">
                    <Paper p="xs" withBorder>
                        <Text size="sm" c="dimmed">Total Processes</Text>
                        <Text size="xl" fw={700}>{processes.length}</Text>
                    </Paper>
                    <Paper p="xs" withBorder>
                        <Text size="sm" c="dimmed">Templates</Text>
                        <Text size="xl" fw={700}>{templates.length}</Text>
                    </Paper>
                    <Paper p="xs" withBorder>
                        <Text size="sm" c="dimmed">Domains</Text>
                        <Text size="xl" fw={700}>{domains.length}</Text>
                    </Paper>
                    <Paper p="xs" withBorder>
                        <Text size="sm" c="dimmed">Data Source</Text>
                        <Badge color="green">Repository Pattern</Badge>
                    </Paper>
                </Group>

                {/* Process Grid */}
                {loading ? (
                    <Text ta="center" c="dimmed" py="xl">
                        Loading processes...
                    </Text>
                ) : displayedProcesses.length === 0 ? (
                    <Text ta="center" c="dimmed" py="xl">
                        No processes found. Create one to get started.
                    </Text>
                ) : (
                    <Grid>
                        {displayedProcesses.map(process => (
                            <Grid.Col key={process.id} span={{ base: 12, sm: 6, lg: 4 }}>
                                <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
                                    <Group justify="space-between" mb="xs">
                                        <Group>
                                            <Title order={4}>{process.name}</Title>
                                            {process.isTemplate && (
                                                <Badge color="violet" leftSection={<IconTemplate size={14} />}>
                                                    Template
                                                </Badge>
                                            )}
                                        </Group>
                                        <Badge color="blue">{process.domain}</Badge>
                                    </Group>

                                    <Text size="sm" c="dimmed" lineClamp={2} mb="md">
                                        {process.description}
                                    </Text>

                                    <Group gap="xs" mb="md">
                                        <Badge variant="light" color={
                                            process.complexity === 'Low' ? 'green' :
                                                process.complexity === 'Medium' ? 'yellow' :
                                                    process.complexity === 'High' ? 'orange' : 'red'
                                        }>
                                            {process.complexity} Complexity
                                        </Badge>
                                        {process.avgDuration && (
                                            <Badge variant="light">
                                                ~{process.avgDuration} min
                                            </Badge>
                                        )}
                                    </Group>

                                    {process.metrics && (
                                        <Group gap="xs" mb="md">
                                            <Text size="xs" c="dimmed">
                                                {process.metrics.totalSteps} steps
                                            </Text>
                                            <Text size="xs" c="dimmed">•</Text>
                                            <Text size="xs" c="dimmed">
                                                {process.metrics.uniqueActors} actors
                                            </Text>
                                            {process.metrics.loopingStages > 0 && (
                                                <>
                                                    <Text size="xs" c="dimmed">•</Text>
                                                    <Text size="xs" c="dimmed">
                                                        {process.metrics.loopingStages} loops
                                                    </Text>
                                                </>
                                            )}
                                        </Group>
                                    )}

                                    <Group>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedProcess(process);
                                                onProcessSelect?.(process);
                                            }}
                                        >
                                            View
                                        </Button>
                                        {process.isTemplate ? (
                                            <Tooltip label="Clone template">
                                                <ActionIcon
                                                    variant="light"
                                                    color="violet"
                                                    onClick={() => handleCloneTemplate(process.id)}
                                                >
                                                    <IconCopy size={18} />
                                                </ActionIcon>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip label="Edit process">
                                                <ActionIcon variant="light">
                                                    <IconEdit size={18} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                        <Tooltip label="Delete">
                                            <ActionIcon
                                                variant="light"
                                                color="red"
                                                onClick={() => handleDeleteProcess(process.id)}
                                                loading={mutationLoading}
                                            >
                                                <IconTrash size={18} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Card>
                            </Grid.Col>
                        ))}
                    </Grid>
                )}
            </Paper>

            {/* Create Process Modal */}
            <Modal
                opened={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Create New Process"
                size="lg"
            >
                <form onSubmit={form.onSubmit(handleCreateProcess)}>
                    <Stack>
                        <TextInput
                            label="Process Name"
                            placeholder="e.g., Healthcare Revenue Cycle"
                            required
                            {...form.getInputProps('name')}
                        />

                        <Select
                            label="Domain"
                            data={['Healthcare', 'Finance', 'Supply Chain', 'HR', 'IT', 'Other']}
                            required
                            {...form.getInputProps('domain')}
                        />

                        <Textarea
                            label="Description"
                            placeholder="Describe what this process does..."
                            rows={3}
                            required
                            {...form.getInputProps('description')}
                        />

                        <Select
                            label="Complexity"
                            data={['Low', 'Medium', 'High', 'Very High']}
                            {...form.getInputProps('complexity')}
                        />

                        <Group justify="flex-end">
                            <Button variant="subtle" onClick={() => setShowCreateModal(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" loading={mutationLoading}>
                                Create Process
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </Container>
    );
};

export default ComplexProcessManagerRefactored;
